import React, { useMemo } from "react";
import styled from "styled-components";
import { Area, AreaChart, CartesianGrid, ReferenceArea, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { colors } from "./colors";
import { addDays, formatCompact, formatDay, formatNumber, formatWeekday } from "./format";

function TrendTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;
    const { date, value, unit } = payload[0].payload;
    return (
        <Box>
            <Muted>{formatWeekday(date)}</Muted>
            <Strong>{formatNumber(value)} {unit}</Strong>
        </Box>
    );
}

/** A drop's measure day by day around the day it started, with the day it started and the days its pages changed. */
export default function TrendChart({ values, unit, breakIndex, trendStart, changeDates = [] }) {
    const data = useMemo(() => values.map((value, i) => ({ date: addDays(trendStart, i), value, unit })), [values, unit, trendStart]);
    const breakDate = data[breakIndex]?.date;
    const lastDate = data[data.length - 1]?.date;
    const uniqueChangeDates = [...new Set(changeDates)].filter((date) => date >= data[0]?.date && date !== breakDate);

    return (
        <Plot role="img" aria-label={`Daily ${unit} around the start, with the start marked`}>
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data} margin={{ top: 20, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke={colors.line} />
                    <XAxis dataKey="date" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: colors.chartSubtle }} tickFormatter={formatDay} minTickGap={40} />
                    <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: colors.chartSubtle }} tickFormatter={formatCompact} width={40} domain={[0, "auto"]} />
                    {breakDate && lastDate && <ReferenceArea x1={breakDate} x2={lastDate} fill={colors.critical} fillOpacity={0.06} strokeOpacity={0} />}
                    {breakDate && (
                        <ReferenceLine x={breakDate} stroke={colors.critical} strokeDasharray="3 3"
                                       label={{ value: "Drop starts", position: "top", fontSize: 11, fill: colors.critical, fontWeight: 600 }} />
                    )}
                    {uniqueChangeDates.map((date) => (
                        <ReferenceLine key={date} x={date} stroke={colors.pageChange} strokeDasharray="2 3"
                                       label={{ value: "Page changed", position: "insideTopLeft", fontSize: 10, fill: colors.pageChange, fontWeight: 600 }} />
                    ))}
                    <Tooltip content={<TrendTooltip />} cursor={{ stroke: colors.lineStrong }} />
                    <Area type="monotone" dataKey="value" stroke={colors.accent} strokeWidth={2} fill={colors.accent} fillOpacity={0.07}
                          isAnimationActive={false} activeDot={{ r: 3.5, stroke: "#FFFFFF", strokeWidth: 2 }} />
                </AreaChart>
            </ResponsiveContainer>
        </Plot>
    );
}

const Plot = styled.div`
    height: 180px;
`;

const Box = styled.div`
    border-radius: 6px;
    border: 1px solid ${colors.line};
    background: ${colors.surface};
    padding: 6px 10px;
    font-size: 12px;
    box-shadow: 0 4px 6px -1px rgba(15, 20, 25, 0.1);
`;

const Muted = styled.p`
    margin: 0;
    color: ${colors.muted};
`;

const Strong = styled.p`
    margin: 0;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: ${colors.ink};
`;
